import { createElement, Fragment } from 'react';
import type { ReactNode } from 'react';

type InlineToken =
  | { type: 'text'; text: string }
  | { type: 'strong'; text: string; em?: boolean }
  | { type: 'em'; text: string }
  | { type: 'code'; text: string };

type MarkdownBlock =
  | { type: 'code'; code: string }
  | { type: 'chart'; json: string }
  | { type: 'form'; json: string }
  | { type: 'loading' }
  | { type: 'table'; rows: string[][] }
  | { type: 'list'; ordered: boolean; items: string[] }
  | { type: 'header'; level: 1 | 2 | 3; text: string }
  | { type: 'blockquote'; blocks: MarkdownBlock[] }
  | { type: 'line'; text: string }
  | { type: 'break' };

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function escapeAttr(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function tokenizeInline(text: string): InlineToken[] {
  const tokens: InlineToken[] = [];
  const pattern = /`([^`]+)`|\*\*\*(.+?)\*\*\*|\*\*(.+?)\*\*|_(.+?)_|\*([^*]+)\*/g;
  let lastIndex = 0;

  for (const match of text.matchAll(pattern)) {
    const matchIndex = match.index ?? 0;
    if (matchIndex > lastIndex) {
      tokens.push({ type: 'text', text: text.slice(lastIndex, matchIndex) });
    }

    if (match[1] !== undefined) {
      tokens.push({ type: 'code', text: match[1] });
    } else if (match[2] !== undefined) {
      tokens.push({ type: 'strong', text: match[2], em: true });
    } else if (match[3] !== undefined) {
      tokens.push({ type: 'strong', text: match[3] });
    } else if (match[4] !== undefined) {
      tokens.push({ type: 'em', text: match[4] });
    } else if (match[5] !== undefined) {
      tokens.push({ type: 'em', text: match[5] });
    }

    lastIndex = matchIndex + match[0].length;
  }

  if (lastIndex < text.length) {
    tokens.push({ type: 'text', text: text.slice(lastIndex) });
  }

  return tokens;
}

function parseMarkdownBlocks(text: string): MarkdownBlock[] {
  let processed = text;

  const codeBlocks: string[] = [];
  const chartBlocks: string[] = [];
  const formBlocks: string[] = [];

  processed = processed.replace(/```(\w*)\n([\s\S]*?)```/g, (_match, lang: string, code: string) => {
    if (lang === 'chart') {
      const idx = chartBlocks.length;
      chartBlocks.push(code.trim());
      return `\x00CH${idx}\x00`;
    }

    if (lang === 'form') {
      const idx = formBlocks.length;
      formBlocks.push(code.trim());
      return `\x00FM${idx}\x00`;
    }

    const idx = codeBlocks.length;
    codeBlocks.push(code.trimEnd());
    return `\x00CB${idx}\x00`;
  });

  processed = processed.replace(/```(?:form|chart)[\s\S]*$/, '\x00LOADING\x00');

  const lines = processed.split('\n');
  return parseLinesIntoBlocks(lines, codeBlocks, chartBlocks, formBlocks);
}

function stripBlockquoteMarker(line: string): string {
  return line.replace(/^\s*>\s?/, '');
}

function parseLinesIntoBlocks(
  lines: string[],
  codeBlocks: string[],
  chartBlocks: string[],
  formBlocks: string[],
): MarkdownBlock[] {
  const blocks: MarkdownBlock[] = [];
  let currentList: { ordered: boolean; items: string[] } | null = null;
  let currentTable: string[][] | null = null;

  const flushList = () => {
    if (!currentList) return;
    blocks.push({
      type: 'list',
      ordered: currentList.ordered,
      items: currentList.items,
    });
    currentList = null;
  };

  const flushTable = () => {
    if (!currentTable) return;
    blocks.push({ type: 'table', rows: currentTable });
    currentTable = null;
  };

  const flushStructured = () => {
    flushList();
    flushTable();
  };

  for (let lineIndex = 0; lineIndex < lines.length; lineIndex++) {
    const line = lines[lineIndex];
    const trimmed = line.trim();

    if (/^>/.test(trimmed)) {
      flushStructured();
      const quoteLines: string[] = [stripBlockquoteMarker(line)];
      let next = lineIndex + 1;
      while (next < lines.length && /^>/.test(lines[next].trim())) {
        quoteLines.push(stripBlockquoteMarker(lines[next]));
        next++;
      }
      blocks.push({
        type: 'blockquote',
        blocks: parseLinesIntoBlocks(quoteLines, codeBlocks, chartBlocks, formBlocks),
      });
      lineIndex = next - 1;
      continue;
    }

    const codeMatch = trimmed.match(/^\x00CB(\d+)\x00$/);
    if (codeMatch) {
      flushStructured();
      blocks.push({ type: 'code', code: codeBlocks[parseInt(codeMatch[1], 10)] });
      continue;
    }

    const chartMatch = trimmed.match(/^\x00CH(\d+)\x00$/);
    if (chartMatch) {
      flushStructured();
      blocks.push({ type: 'chart', json: chartBlocks[parseInt(chartMatch[1], 10)] });
      continue;
    }

    const formMatch = trimmed.match(/^\x00FM(\d+)\x00$/);
    if (formMatch) {
      flushStructured();
      blocks.push({ type: 'form', json: formBlocks[parseInt(formMatch[1], 10)] });
      continue;
    }

    if (trimmed.includes('\x00LOADING\x00')) {
      flushStructured();
      blocks.push({ type: 'loading' });
      continue;
    }

    if (trimmed.startsWith('|') && trimmed.endsWith('|')) {
      flushList();
      const cells = trimmed.slice(1, -1).split('|').map((cell) => cell.trim());

      if (cells.every((cell) => /^[-:]+$/.test(cell))) {
        continue;
      }

      if (!currentTable) {
        currentTable = [cells];
      } else {
        currentTable.push(cells);
      }
      continue;
    }

    flushTable();

    const ulMatch = trimmed.match(/^[-*]\s+(.*)/);
    if (ulMatch) {
      if (!currentList || currentList.ordered) {
        flushList();
        currentList = { ordered: false, items: [] };
      }
      currentList.items.push(ulMatch[1]);
      continue;
    }

    const olMatch = trimmed.match(/^\d+\.\s+(.*)/);
    if (olMatch) {
      if (!currentList || !currentList.ordered) {
        flushList();
        currentList = { ordered: true, items: [] };
      }
      currentList.items.push(olMatch[1]);
      continue;
    }

    flushList();

    if (trimmed.startsWith('### ')) {
      blocks.push({ type: 'header', level: 3, text: trimmed.slice(4) });
    } else if (trimmed.startsWith('## ')) {
      blocks.push({ type: 'header', level: 2, text: trimmed.slice(3) });
    } else if (trimmed.startsWith('# ')) {
      blocks.push({ type: 'header', level: 1, text: trimmed.slice(2) });
    } else if (trimmed === '') {
      blocks.push({ type: 'break' });
    } else {
      blocks.push({ type: 'line', text: line });
    }
  }

  flushStructured();

  while (blocks.length > 0 && blocks[blocks.length - 1]?.type === 'break') {
    blocks.pop();
  }

  return blocks;
}

function renderInlineReact(text: string, keyPrefix: string): ReactNode[] {
  return tokenizeInline(text).map((token, index) => {
    const key = `${keyPrefix}-${index}`;
    switch (token.type) {
      case 'strong':
        return token.em
          ? createElement('strong', { key }, createElement('em', null, token.text))
          : createElement('strong', { key }, token.text);
      case 'em':
        return createElement('em', { key }, token.text);
      case 'code':
        return createElement('code', { key, className: 'md-code' }, token.text);
      default:
        return token.text;
    }
  });
}

function renderInlineHtml(text: string): string {
  return tokenizeInline(text)
    .map((token) => {
      switch (token.type) {
        case 'strong':
          return token.em
            ? `<strong><em>${escapeHtml(token.text)}</em></strong>`
            : `<strong>${escapeHtml(token.text)}</strong>`;
        case 'em':
          return `<em>${escapeHtml(token.text)}</em>`;
        case 'code':
          return `<code class="md-code">${escapeHtml(token.text)}</code>`;
        default:
          return escapeHtml(token.text);
      }
    })
    .join('');
}

function renderExpandButtonReact(type: 'chart' | 'table', key: string): ReactNode {
  return createElement(
    'button',
    {
      key,
      className: 'expand-icon',
      'data-expand': type,
      'aria-label': 'Expand',
      type: 'button',
    },
    createElement(
      'svg',
      {
        width: 14,
        height: 14,
        viewBox: '0 0 24 24',
        fill: 'none',
        stroke: 'currentColor',
        strokeWidth: 2,
        strokeLinecap: 'round',
        strokeLinejoin: 'round',
      },
      createElement('polyline', { points: '15 3 21 3 21 9' }),
      createElement('polyline', { points: '9 21 3 21 3 15' }),
      createElement('line', { x1: 21, y1: 3, x2: 14, y2: 10 }),
      createElement('line', { x1: 3, y1: 21, x2: 10, y2: 14 }),
    ),
  );
}

function renderExpandButtonHtml(type: 'chart' | 'table'): string {
  return `<button class="expand-icon" data-expand="${type}" aria-label="Expand" type="button"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 3 21 3 21 9"></polyline><polyline points="9 21 3 21 3 15"></polyline><line x1="21" y1="3" x2="14" y2="10"></line><line x1="3" y1="21" x2="10" y2="14"></line></svg></button>`;
}

function renderBlocksReact(blocks: MarkdownBlock[], keyPrefix: string): ReactNode[] {
  const nodes: ReactNode[] = [];

  blocks.forEach((block, index) => {
    const key = `${keyPrefix}-${index}`;

    switch (block.type) {
      case 'code':
        nodes.push(
          createElement(
            'pre',
            { key, className: 'md-code-block' },
            createElement('code', null, block.code),
          ),
        );
        break;

      case 'chart':
        nodes.push(
          createElement(
            'div',
            { key, className: 'chart-container', 'data-chart-spec': block.json },
            renderExpandButtonReact('chart', `${key}-btn`),
          ),
        );
        break;

      case 'form':
        nodes.push(
          createElement('div', {
            key,
            className: 'form-container',
            'data-form-spec': block.json,
          }),
        );
        break;

      case 'loading':
        nodes.push(
          createElement(
            'div',
            { key, className: 'form-loading' },
            createElement('span', { className: 'form-loading-spinner' }),
          ),
        );
        break;

      case 'table': {
        const [headerRow, ...bodyRows] = block.rows;
        nodes.push(
          createElement(
            'div',
            { key, className: 'md-table-wrap', style: { position: 'relative' } },
            renderExpandButtonReact('table', `${key}-btn`),
            createElement(
              'table',
              { className: 'md-table' },
              createElement(
                'thead',
                null,
                createElement(
                  'tr',
                  null,
                  headerRow.map((cell, cellIndex) =>
                    createElement(
                      'th',
                      { key: `${key}-h-${cellIndex}` },
                      ...renderInlineReact(cell, `${key}-h-${cellIndex}`),
                    ),
                  ),
                ),
              ),
              createElement(
                'tbody',
                null,
                bodyRows.map((row, rowIndex) =>
                  createElement(
                    'tr',
                    { key: `${key}-r-${rowIndex}` },
                    row.map((cell, cellIndex) =>
                      createElement(
                        'td',
                        { key: `${key}-r-${rowIndex}-c-${cellIndex}` },
                        ...renderInlineReact(cell, `${key}-r-${rowIndex}-c-${cellIndex}`),
                      ),
                    ),
                  ),
                ),
              ),
            ),
          ),
        );
        break;
      }

      case 'list':
        nodes.push(
          createElement(
            block.ordered ? 'ol' : 'ul',
            { key, className: 'md-list' },
            block.items.map((item, itemIndex) =>
              createElement(
                'li',
                { key: `${key}-item-${itemIndex}` },
                ...renderInlineReact(item, `${key}-item-${itemIndex}`),
              ),
            ),
          ),
        );
        break;

      case 'header':
        nodes.push(
          createElement(
            'div',
            { key, className: `md-h${block.level}` },
            ...renderInlineReact(block.text, key),
          ),
        );
        break;

      case 'blockquote':
        nodes.push(
          createElement(
            'blockquote',
            { key, className: 'md-blockquote' },
            ...renderBlocksReact(block.blocks, `${key}-bq`),
          ),
        );
        break;

      case 'line':
        nodes.push(
          createElement(
            Fragment,
            { key },
            ...renderInlineReact(block.text, key),
            index < blocks.length - 1 ? createElement('br', { key: `${key}-br` }) : null,
          ),
        );
        break;

      case 'break':
        nodes.push(createElement('br', { key }));
        break;
    }
  });

  return nodes;
}

export function renderMarkdown(text: string, isStreaming = false): ReactNode {
  const blocks = parseMarkdownBlocks(text);
  const nodes = renderBlocksReact(blocks, 'md');

  if (isStreaming) {
    nodes.push(createElement('span', { key: 'md-stream-cursor', className: 'typing-cursor' }, '|'));
  }

  return createElement(Fragment, null, ...nodes);
}

function renderBlocksToHtml(blocks: MarkdownBlock[]): string {
  return blocks
    .map((block, index) => {
      switch (block.type) {
        case 'code':
          return `<pre class="md-code-block"><code>${escapeHtml(block.code)}</code></pre>`;

        case 'chart':
          return `<div class="chart-container" data-chart-spec="${escapeAttr(block.json)}">${renderExpandButtonHtml('chart')}</div>`;

        case 'form':
          return `<div class="form-container" data-form-spec="${escapeAttr(block.json)}"></div>`;

        case 'loading':
          return '<div class="form-loading"><span class="form-loading-spinner"></span></div>';

        case 'table': {
          const [headerRow, ...bodyRows] = block.rows;
          const headHtml = headerRow.map((cell) => `<th>${renderInlineHtml(cell)}</th>`).join('');
          const bodyHtml = bodyRows
            .map((row) => `<tr>${row.map((cell) => `<td>${renderInlineHtml(cell)}</td>`).join('')}</tr>`)
            .join('');
          return `<div class="md-table-wrap" style="position:relative;">${renderExpandButtonHtml('table')}<table class="md-table"><thead><tr>${headHtml}</tr></thead><tbody>${bodyHtml}</tbody></table></div>`;
        }

        case 'list': {
          const tag = block.ordered ? 'ol' : 'ul';
          const itemsHtml = block.items.map((item) => `<li>${renderInlineHtml(item)}</li>`).join('');
          return `<${tag} class="md-list">${itemsHtml}</${tag}>`;
        }

        case 'header':
          return `<div class="md-h${block.level}">${renderInlineHtml(block.text)}</div>`;

        case 'blockquote':
          return `<blockquote class="md-blockquote">${renderBlocksToHtml(block.blocks)}</blockquote>`;

        case 'line':
          return renderInlineHtml(block.text) + (index < blocks.length - 1 ? '<br>' : '');

        case 'break':
          return '<br>';
      }
    })
    .join('');
}

export function renderMarkdownToHtml(text: string, isStreaming = false): string {
  const blocks = parseMarkdownBlocks(text);
  const html = renderBlocksToHtml(blocks);

  return isStreaming ? `${html}<span class="typing-cursor">|</span>` : html;
}

/**
 * Replace a container's innerHTML while keeping any already-portaled .form-container
 * node's live DOM (and the React portal mounted inside it) intact, instead of
 * destroying and recreating it.
 *
 * Each preserved node's real identity is that it already carries a unique
 * data-form-portal-id (assigned once, by MessageList's discovery effect, and never
 * regenerated for the lifetime of that DOM node). data-form-spec is NOT used as an
 * identity key here - it's only content, and two form-container instances can share
 * an identical spec (e.g. the same tool's form appearing twice). Keying a lookup map
 * by spec alone would let one instance silently overwrite the other in the map,
 * losing a live portal. Instead, preserved nodes are kept as a list and paired with
 * fresh placeholders positionally *within same-spec groups*, in document order -
 * correct even with duplicate specs, since streaming only appends content and never
 * reorders/removes earlier form blocks.
 *
 * Detaching a node via innerHTML = on its parent does not destroy it - a
 * still-referenced detached node can be reinserted elsewhere in the document and its
 * subtree, including a mounted React portal, survives untouched as long as the node
 * object identity is preserved.
 */
export function setHtmlPreservingFormPortals(container: HTMLElement, html: string): void {
  const preserved: HTMLElement[] = Array.from(
    container.querySelectorAll('.form-container[data-form-portal-id]'),
  ) as HTMLElement[];

  container.innerHTML = html;
  if (preserved.length === 0) return;

  const freshBySpec = new Map<string, HTMLElement[]>();
  container.querySelectorAll('.form-container').forEach((fc) => {
    const spec = (fc as HTMLElement).getAttribute('data-form-spec');
    if (!spec) return;
    const list = freshBySpec.get(spec) ?? [];
    list.push(fc as HTMLElement);
    freshBySpec.set(spec, list);
  });

  for (const liveNode of preserved) {
    const spec = liveNode.getAttribute('data-form-spec');
    const candidates = spec ? freshBySpec.get(spec) : undefined;
    const fresh = candidates?.shift(); // claim+consume the next unmatched same-spec placeholder
    if (fresh) fresh.replaceWith(liveNode);
  }
}
